import type { QuickTemplateInfo, QuickTranslateFn } from './core';
import { createTq } from './core';

export interface I18nextLikeTranslator {
  (key: string, defaultValue?: string, options?: Record<string, unknown>): string;
}

// Options for creating an i18next-powered tq instance.
// All fields have defaults in DEFAULT_I18NEXT_TQ_OPTIONS.
export interface CreateI18nextTqOptions {
  readonly namespace: string;
  readonly baseOptions: Record<string, unknown>;
  readonly onMissing: (details: { key: string; info: QuickTemplateInfo }) => void;
  readonly debug: (details: { stage: 'plural'; key: string; hashInput: string; info: QuickTemplateInfo }) => void;
  readonly locale: string | undefined;
  readonly resolveLocale?: () => string | undefined;
  readonly i18n?: { language: string };
}

export const DEFAULT_I18NEXT_TQ_OPTIONS: CreateI18nextTqOptions = {
  namespace: 'quick',
  baseOptions: {},
  onMissing: () => {},
  debug: () => {},
  locale: undefined,
  resolveLocale: undefined,
  i18n: undefined,
};

export const createI18nextTranslate = (
  t: I18nextLikeTranslator,
  options: CreateI18nextTqOptions,
): QuickTranslateFn<string> => (key, info) => {
  const mergedOptions = { ...options.baseOptions, ...info.variables };
  return t(key, info.fallbackText, mergedOptions);
};

export const createI18nextTq = (t: I18nextLikeTranslator, options: Partial<CreateI18nextTqOptions> = {}) => {
  const mergedOptions: CreateI18nextTqOptions = { ...DEFAULT_I18NEXT_TQ_OPTIONS, ...options };
  const baseOptionsLocale = typeof mergedOptions.baseOptions.lng === 'string' ? mergedOptions.baseOptions.lng : undefined;
  const resolveLocale = mergedOptions.resolveLocale ?? (() => mergedOptions.i18n?.language ?? mergedOptions.locale ?? baseOptionsLocale);
  const staticLocale = mergedOptions.locale ?? baseOptionsLocale;
  const initialLocale = resolveLocale();

  if (!initialLocale) {
    throw new Error(
      'createI18nextTq: Unable to determine locale. Provide `locale`, `baseOptions.lng`, `resolveLocale`, or pass an i18next instance via options.i18n.',
    );
  }
  
  return createTq({
    translate: createI18nextTranslate(t, mergedOptions),
    namespace: mergedOptions.namespace,
    onMissing: mergedOptions.onMissing,
    locale: staticLocale,
    resolveLocale,
    // Do not treat returned key as missing; leave fallback to i18next
    treatReturnedKeyAsMissing: false,
    debug: mergedOptions.debug,
  });
};
